import React from 'react';

import { deriveCaseManagementValues } from '~support-at-home/lib/derive';

import { Currency } from '~components/form-fields/inputs';

import { Supplement } from '~lookups/models';
import { SectionTotal } from '~support-at-home/models';

import { useGetLookupSupplementsQuery } from '~lookups/actions/supplements';

interface ComponentProps {
  id: string;
  amount?: number;
  totals?: SectionTotal;
  includesCaseManagement?: boolean;
  supplements?: Supplement[];
  showCaseManagement?: boolean;
}

interface CurrencyRowProps {
  id: string;
  label: string;
  value?: number;
  indented?: boolean;
  additionalClass?: string;
}

const CurrencyRow: React.FC<CurrencyRowProps> = ({ id, label, value, indented = false, additionalClass = '' }) => (
  <div className="row">
    <div className={`label${indented ? ' ms-5 text-end' : ''}${additionalClass ? ` ${additionalClass}` : ''}`}>
      {label}
    </div>
    <div className="value">
      <Currency id={id} name={id} type="currency" value={value} disabled noDecimals />
    </div>
  </div>
);

const BudgetGrandTotal: React.FC<ComponentProps> = ({
  id,
  totals,
  amount,
  includesCaseManagement,
  supplements,
  showCaseManagement = false,
}) => {
  const { cost, contribution, govSubsidy } = totals || {};
  const { data: supplementRates } = useGetLookupSupplementsQuery();

  const { caseManagementAmount } = deriveCaseManagementValues({
    total_amount: amount,
    total_includes_case_management: includesCaseManagement,
  });

  const cmSupplements =
    supplements?.filter((s) =>
      supplementRates?.find((rate) => rate.id === s.supplement_id && rate.type === 'casemanagement'),
    ) ?? [];

  const cmSupplementTotal = cmSupplements.reduce((total, supplement) => total + (supplement.amount || 0), 0);
  const totalCaseManagement = cmSupplementTotal + caseManagementAmount;

  return (
    <div className="grand-total-container" id={id}>
      {showCaseManagement && (
        <div className="row overline px-0 py-2">
          <CurrencyRow
            id="grand-total-case-management"
            label="Case Management"
            value={totalCaseManagement}
            additionalClass="additional"
          />
          <hr className="w-50 mx-auto" />
          <CurrencyRow id="grand-total-case-management-base" label="Base" value={caseManagementAmount} indented />
          <CurrencyRow
            id="grand-total-case-management-supplements"
            label="Supplements"
            value={cmSupplementTotal}
            indented
          />
        </div>
      )}
      <div className="total-lines" />
      <CurrencyRow id="grand-total-cost" label="Grand Total" value={cost} />
      <hr className="w-50 mx-auto" />
      <CurrencyRow id="grand-total-contribution" label="Co-Contribution" value={contribution} indented />
      <CurrencyRow id="grand-total-govSubsidy" label="Gov. Contribution" value={govSubsidy} indented />
    </div>
  );
};

export default BudgetGrandTotal;
